// Sfx — the whole procedural sound palette.
// No audio assets: every cue is synthesized from
// ONE voice family ("struck bone"): detuned sine partial stacks at bone-bar
// ratios with modal damping (high partials die faster), shared attack/decay
// envelopes, band-passed noise transients, and a brightness-aware soft
// saturator. Melodic cues share an A-minor pentatonic. Cues are
// loudness-normalized on an HF-weighted RMS, hard peak ceiling, no clipping,
// and no partial may alias even at max runtime pitch bend.
// Frequently-repeated cues render several seeded variants, played round-robin.
// ALL event tuning (cue choice + per-event trim dB) lives in ONE table
// (SOUNDS); TIER_RULES binds an event's frequency to its sound's size.
// Graph: cue source -> trim gain -> [work bus (duckable)] -> sfx bus
// (master volume/mute) -> dry + small-room convolver wet -> destination.
// Mute + master volume persist in the shared localStorage [audio] blob.

import {
  getCtx, onUnlock, armUnlock, loadAudioSettings, saveAudioSettings,
  mulberry32, strHash, type Rng,
} from './ctx';

export const RATE = 44100;
/** Highest runtime pitch a cue can see (hit pitch tops out at 1.15, work walk
    at 1.06) — partials that would cross Nyquist at this bend are culled. */
const PITCH_HEADROOM = 1.2;

// Audio tunables (palette.gd AUDIO_* mirrored — not theme colors).
const RMS_DB = -16.0; // per-cue loudness target (HF-weighted RMS)
const PEAK = 0.85; // hard per-cue peak ceiling (linear, < 1.0)
const SOFT_DRIVE = 1.6; // soft-saturator drive (the family "glue")
const HF_HZ = 1000.0; // loudness-weighting band split
const HF_WEIGHT = 2.5; // content above split counts this × hotter
const SAT_LO_HZ = 350.0; // full tanh warmth at/below this brightness
const SAT_HI_HZ = 1400.0; // no saturation at/above (ticks stay clean)
const REVERB_WET = 0.09; // small-room glue on the sfx bus
const DEFAULT_VOLUME = 0.8; // master slider default (0..1)
const DUCK_DB = -10.0; // celebration duck depth on the work bus
const DUCK_HOLD_S = 0.3;
const DUCK_RECOVER_S = 0.9;
const WALK_STEP = 0.02; // per-cycle pitch random-walk step
const WALK_MAX = 0.06; // pitch walk bound (±6%)

const TAU = Math.PI * 2;

// Material mode tables: [freq ratio, amplitude, decay 1/s] per mode, MEASURED
// from real recordings (see sfx.gd) — only these numbers ship, never audio.
export const BAR: number[][] = [
  [0.943, 0.28, 85.0], [1.0, 1.0, 50.0], [1.055, 0.08, 51.0],
  [1.79, 0.07, 45.0], [2.21, 0.06, 65.0], [2.72, 0.1, 58.0],
  [3.14, 0.07, 52.0], [5.7, 0.05, 19.0]];
export const METAL: number[][] = [
  [1.0, 1.0, 49.0], [1.053, 0.085, 49.0], [1.847, 0.411, 20.0],
  [2.534, 0.496, 16.0], [3.005, 0.114, 39.0], [4.646, 0.087, 29.0],
  [6.514, 0.105, 26.0]];
export const BELL: number[][] = [
  [0.707, 0.105, 66.0], [1.0, 1.0, 12.0], [2.305, 0.06, 4.0],
  [3.765, 0.201, 7.0], [7.18, 0.148, 34.0]];
export const DARK: number[][] = [
  [1.0, 1.0, 5.0], [1.504, 0.183, 5.0], [1.924, 0.334, 7.0],
  [2.924, 0.086, 5.0], [3.303, 0.094, 11.0], [6.101, 0.075, 10.0]];

/** A-minor pentatonic from A3; note(i) walks it upward. */
const PENTA = [0, 3, 5, 7, 10];
const ROOT_HZ = 220.0;

/** THE tuning table — one row per audible EVENT (see sfx.gd for the tier
    contract: the more often something fires, the smaller its sound). */
export const TIER_RULES: Record<string, [number, number]> = {
  constant: [0.15, -6.0],
  frequent: [0.45, 0.0],
  occasional: [1.0, 0.5],
  rare: [2.0, 3.0],
};
export const SOUNDS: Record<string, { cue?: string; db: number; tier: string }> = {
  // steady tickers — single soft contacts, never phrases
  ui_tick: { db: -8.0, tier: 'constant' },
  ui_tab: { db: -8.0, tier: 'constant' },
  ui_tap: { cue: 'ui_tick', db: -12.0, tier: 'constant' }, // non-button pressables (slots/rows/tiles)
  work_cycle: { cue: 'work_*', db: -10.0, tier: 'constant' },
  items_gained: { cue: 'pluck', db: -14.0, tier: 'constant' },
  xp_gained: { cue: 'xp', db: -10.0, tier: 'constant' },
  // combat bursts
  hit_out: { db: -3.0, tier: 'frequent' },
  hit_in: { db: -2.0, tier: 'frequent' },
  miss: { db: -5.0, tier: 'frequent' },
  block: { db: -4.0, tier: 'frequent' },
  deny_soft: { db: -6.0, tier: 'frequent' },
  // occasional beats
  eat: { db: -4.0, tier: 'occasional' },
  heal: { db: -8.0, tier: 'occasional' },
  gather_empty: { cue: 'empty', db: -4.0, tier: 'occasional' },
  arrived: { db: -2.0, tier: 'occasional' },
  fled: { db: -2.0, tier: 'occasional' },
  alert: { cue: 'deny', db: -1.0, tier: 'occasional' },
  kill: { db: 0.0, tier: 'occasional' },
  break_soft: { db: -3.0, tier: 'occasional' },
  quest_step: { db: -1.0, tier: 'occasional' },
  claim: { db: 0.0, tier: 'occasional' },
  volume_preview: { cue: 'ui_tick', db: 0.0, tier: 'occasional' },
  // rare celebrations/disasters — the only polyphonic rings in the palette
  level_up: { db: 0.0, tier: 'rare' },
  level_up_big: { db: 0.0, tier: 'rare' },
  fanfare: { db: 0.0, tier: 'rare' },
  quest_start: { db: 0.0, tier: 'rare' },
  quest_done: { db: 0.0, tier: 'rare' },
  death: { db: 0.0, tier: 'rare' },
  break_alarm: { db: 2.0, tier: 'rare' },
};

/** Parent skills (both streams and parents server-side) get the big arpeggio. */
const PARENT_SKILLS = ['gathering', 'crafting', 'combat'];

/** SkillId -> work family (fallback when the activity name isn't telling). */
const SKILL_FAMILY: Record<string, string> = {
  gathering: 'gather', mining: 'mine', woodcutting: 'chop',
  fishing: 'fish', smithing: 'smith',
  fletching: 'fletch', cooking: 'cook', leatherworking: 'sew',
  tailoring: 'sew', crafting: 'craft', moulding: 'mould',
};

/** Activity-name verbs -> family. Order matters (smelt before smith checks). */
const VERB_FAMILY: [string, string][] = [
  ['smelt', 'smelt'], ['forge', 'smith'], ['smith', 'smith'],
  ['whittle', 'whittle'], ['knap', 'knap'], ['fletch', 'fletch'],
  ['cook', 'cook'], ['roast', 'cook'], ['bake', 'cook'],
  ['sew', 'sew'], ['weave', 'sew'], ['tan ', 'sew'], ['stitch', 'sew'],
  ['mould', 'mould'], ['mold', 'mould'],
  ['mine', 'mine'], ['chop', 'chop'], ['fell', 'chop'], ['fish', 'fish'],
  ['hunt', 'hunt'], ['gather', 'gather'], ['forage', 'gather'],
  ['dig', 'gather'], ['clip', 'gather'],
  ['pick ', 'gather'],
];

const WORK_FAMILIES = ['gather', 'mine', 'chop', 'fish', 'hunt', 'smelt',
  'smith', 'whittle', 'knap', 'fletch', 'cook', 'sew', 'mould', 'craft'];

/** Cues heard over and over get several renders (fresh noise + micro-detune),
    rotated per play. Work cues are added programmatically. */
const VARIANT_CUES = ['xp', 'pluck', 'hit_out', 'hit_in', 'ui_tick', 'eat'];
const VARIANT_TUNES = [1.0, 0.9885, 1.0116];

export let muted = false;
export let volume = DEFAULT_VOLUME;

// Test/observability hooks.
export let lastPlayed = '';
export let playCount = 0;

type Recipe = () => Float32Array;
const recipes: Record<string, Recipe> = {};
const rendered: Record<string, Float32Array[]> = {};
const abufs: Record<string, AudioBuffer[]> = {};
let variantTune = 1.0; // whole-cue tune scale tone() reads during render

// Bus nodes (built once the shared ctx unlocks).
let sfxBus: GainNode | null = null;
let workBus: GainNode | null = null;

// Work-loop context: family + seeded pitch/timing random walk.
let workFamily = 'craft';
let workRng: Rng = mulberry32(1);
let workWalk = 0.0;
let lastWorkMs = -100000;
let lastXpFamily = '';
const firsts: Record<string, boolean> = {};

export function init(): void {
  const s = loadAudioSettings();
  muted = !!s.muted;
  volume = Math.max(0, Math.min(1, Number(s.volume ?? DEFAULT_VOLUME)));
  armUnlock();
  onUnlock(() => {
    setupBuses();
    // Prewarm in idle slices: work cues + the constant UI ticks first.
    const order = ['ui_tick', 'ui_tab', 'pluck', 'xp', 'deny',
      ...WORK_FAMILIES.map((f) => 'work_' + f),
      ...Object.keys(recipes)];
    const queue = [...new Set(order)];
    const step = () => {
      const k = queue.shift();
      if (!k) return;
      ensure(k);
      setTimeout(step, 30);
    };
    setTimeout(step, 200);
  });
}

function setupBuses(): void {
  const ctx = getCtx();
  if (!ctx || sfxBus) return;
  sfxBus = ctx.createGain();
  workBus = ctx.createGain();
  workBus.connect(sfxBus);
  sfxBus.connect(ctx.destination); // dry
  // A whisper of small-room reverb: dry synth blips read as "test tone";
  // the same blips in a room read as an instrument.
  const conv = ctx.createConvolver();
  conv.buffer = roomImpulse(ctx);
  const wet = ctx.createGain();
  wet.gain.value = REVERB_WET;
  sfxBus.connect(conv);
  conv.connect(wet);
  wet.connect(ctx.destination);
  applyVolume();
}

/** Small-room impulse: 0.35s exponentially-decaying noise, one-pole damped. */
function roomImpulse(ctx: AudioContext): AudioBuffer {
  const n = Math.floor(0.35 * ctx.sampleRate);
  const buf = ctx.createBuffer(2, n, ctx.sampleRate);
  const rng = mulberry32(1234);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let y = 0;
    for (let i = 0; i < n; i++) {
      const t = i / ctx.sampleRate;
      y += 0.35 * (rng() * 2 - 1 - y); // damping low-pass
      d[i] = y * Math.exp(-t * 14.0);
    }
  }
  return buf;
}

// ---------------------------------------------------------------- playback

/** Play by EVENT key: cue + volume come from the SOUNDS table, so all tuning
    lives in one place. Unregistered keys fall back to a quiet raw cue. */
export function playEvent(ev: string, pitch = 1.0): void {
  const spec = SOUNDS[ev];
  if (!spec) {
    play(ev, -6.0, pitch);
    return;
  }
  play(spec.cue ?? ev, spec.db, pitch);
}

/** Play a cue. volumeDb is a per-call trim on top of the normalized cue;
    pitch bends the whole cue (1.0 = as rendered). */
export function play(name: string, volumeDb = 0.0, pitch = 1.0): void {
  if (muted || !recipes[name]) return;
  lastPlayed = name;
  playCount++;
  fire(name, volumeDb, Math.max(0.5, Math.min(pitch, PITCH_HEADROOM)), sfxBus);
}

function fire(name: string, volumeDb: number, pitch: number, bus: GainNode | null): void {
  const ctx = getCtx();
  if (!ctx || !bus || ctx.state !== 'running') return;
  ensure(name);
  const arr = abufs[name];
  if (!arr?.length) return;
  const src = ctx.createBufferSource();
  src.buffer = arr[playCount % arr.length];
  src.playbackRate.value = pitch;
  const g = ctx.createGain();
  g.gain.value = Math.pow(10, volumeDb / 20);
  src.connect(g);
  g.connect(bus);
  src.start();
  src.onended = () => {
    src.disconnect();
    g.disconnect();
  };
}

/** The per-cycle work sound: family cue on the duckable work bus, with a
    seeded random-walk pitch bend and a whisper of timing jitter so hour-long
    loops never sound like a metronome. */
export function playWork(nameHint = ''): void {
  if (muted) return;
  let fam = workFamily;
  if (nameHint !== '') fam = familyFor(nameHint, '');
  let cue = 'work_' + fam;
  if (!recipes[cue]) cue = 'work_craft';
  workWalk = Math.max(-WALK_MAX, Math.min(WALK_MAX,
    workWalk + (workRng() * 2 - 1) * WALK_STEP));
  const delay = workRng() * 0.028;
  const pitch = 1.0 + workWalk;
  lastPlayed = cue;
  playCount++;
  lastWorkMs = performance.now();
  const db = SOUNDS['work_cycle'].db;
  if (delay > 0.004) setTimeout(() => fire(cue, db, pitch, workBus), delay * 1000);
  else fire(cue, db, pitch, workBus);
}

/** Called on every activity rebind: reseed the walk per activity so the
    variation pattern is stable for a task but fresh across tasks. */
export function setWorkContext(activityName: string, icon: string): void {
  const fam = familyFor(activityName, icon);
  if (fam === workFamily) return;
  workFamily = fam;
  workRng = mulberry32(strHash(activityName + icon));
  workWalk = 0.0;
}

/** xp fx names the skill — remember it as the family fallback (crafts don't
    carry a verb the client can always parse). */
export function noteXpSkill(skill: string): void {
  if (SKILL_FAMILY[skill]) lastXpFamily = SKILL_FAMILY[skill];
}

/** Public family resolver (Music's situation detector reuses it). */
export function familyFor(activityName: string, icon: string): string {
  const n = activityName.toLowerCase();
  for (const [verb, fam] of VERB_FAMILY) if (n.includes(verb)) return fam;
  if (icon === 'glyph_fish') return 'fish';
  if (icon === 'glyph_stand') return 'gather';
  if (lastXpFamily !== '') return lastXpFamily;
  return 'craft';
}

// ---------------------------------------------------------------- fx wiring

/** One hook in the fx pump routes every audible game moment here, so sounds
    inherit the fx batching rules. */
export function onFx(f: Record<string, any>): void {
  const t = String(f.type ?? f.fx ?? '');
  switch (t) {
    case 'items_gained':
      break; // per-item flight plucks in world.ts carry this one
    case 'xp_gained':
      noteXpSkill(String(f.skill ?? ''));
      // the work cue just marked this cycle; don't double-tick
      if (performance.now() - lastWorkMs > 350) playEvent('xp_gained');
      break;
    case 'level_up':
      duck();
      if (firstTime('level_up')) playEvent('fanfare');
      else if (PARENT_SKILLS.includes(String(f.skill ?? ''))) playEvent('level_up_big');
      else playEvent('level_up');
      break;
    case 'item_broke':
      // quiet-swap crack vs the LOUD bare-slot alarm (armor-bare moment)
      playEvent(f.replaced ? 'break_soft' : 'break_alarm');
      break;
    case 'hit':
      playHit(f);
      break;
    case 'kill':
      duck();
      playEvent(firstTime('kill') ? 'fanfare' : 'kill');
      break;
    case 'death':
      duck();
      playEvent('death');
      break;
    case 'fled':
      playEvent('fled');
      break;
    case 'quest_started':
      playEvent('quest_start');
      break;
    case 'quest_step_done':
      playEvent('quest_step');
      break;
    case 'quest_complete':
      duck();
      playEvent(firstTime('quest_complete') ? 'fanfare' : 'quest_done');
      break;
    case 'alert':
      playEvent('alert');
      break;
    case 'arrived':
      playEvent('arrived');
      break;
    case 'gather_empty':
      playEvent('gather_empty');
      break;
    case 'eat':
      playEvent('eat');
      if (Number(f.heal ?? 0) >= 3) playEvent('heal');
      break;
  }
}

/** Stoneheim's loudness law (sound.js hit): pitch stays FIXED — whose blood
    it was reads from the out/in cue split — and the hit lands LOUDER the
    harder it hit (noise gain 0.16 + 0.03·amount, capped at 0.34), here as a
    dB trim relative to the cap. Block/miss keep their low/high pitch split. */
function playHit(f: Record<string, any>): void {
  const incoming = String(f.who ?? 'player') === 'creature';
  const amount = Number(f.amount ?? 0);
  switch (String(f.kind ?? 'hit')) {
    case 'miss':
      playEvent('miss', incoming ? 0.9 : 1.0);
      break;
    case 'block':
      playEvent('block', incoming ? 0.85 : 1.0);
      break;
    default: {
      const cue = incoming ? 'hit_in' : 'hit_out';
      const gain = Math.min(0.34, 0.16 + 0.03 * amount);
      play(cue, (SOUNDS[cue]?.db ?? -3.0) + 20 * Math.log10(gain / 0.34), 1.0);
    }
  }
}

/** Celebrations push the idle work loop down for a breath, then recover. */
export function duck(): void {
  const ctx = getCtx();
  if (!ctx || !workBus) return;
  const now = ctx.currentTime;
  const g = workBus.gain;
  g.cancelScheduledValues(now);
  g.setValueAtTime(Math.pow(10, DUCK_DB / 20), now);
  g.setValueAtTime(Math.pow(10, DUCK_DB / 20), now + DUCK_HOLD_S);
  g.linearRampToValueAtTime(1.0, now + DUCK_HOLD_S + DUCK_RECOVER_S);
}

// ---------------------------------------------------------------- settings

export function setMuted(m: boolean, save = true): void {
  muted = m;
  applyVolume();
  if (save) saveAudioSettings({ muted });
}

export function setVolume(v: number, save = true): void {
  volume = Math.max(0, Math.min(1, v));
  applyVolume();
  if (save) saveAudioSettings({ volume });
}

function applyVolume(): void {
  if (sfxBus) sfxBus.gain.value = muted ? 0 : volume;
}

/** One-shot celebration memory (first kill/level/quest get the fanfare).
    Per-session on purpose: each sitting opens with one big moment per kind. */
export function firstTime(key: string): boolean {
  if (firsts[key]) return false;
  firsts[key] = true;
  return true;
}

// ---------------------------------------------------------------- registry

function variantsFor(name: string): number {
  return VARIANT_CUES.includes(name) || name.startsWith('work_') ? VARIANT_TUNES.length : 1;
}

function ensure(name: string): void {
  if (rendered[name]) {
    ensureBufs(name);
    return;
  }
  const arr: Float32Array[] = [];
  for (let v = 0; v < variantsFor(name); v++) {
    variantTune = VARIANT_TUNES[v];
    arr.push(finish(recipes[name]()));
  }
  variantTune = 1.0;
  rendered[name] = arr;
  ensureBufs(name);
}

function ensureBufs(name: string): void {
  const ctx = getCtx();
  if (!ctx || abufs[name]) return;
  abufs[name] = rendered[name].map((data) => {
    const b = ctx.createBuffer(1, data.length, RATE);
    b.copyToChannel(data as Float32Array<ArrayBuffer>, 0);
    return b;
  });
}

// -------------------------------------------------- registry / test hooks
// Mirrors the sfx.gd observability surface (throwaway scratch tests use
// these; render paths are pure math, no AudioContext needed).

export function hasCue(name: string): boolean {
  return !!recipes[name];
}

export function cueNames(): string[] {
  return Object.keys(recipes);
}

/** Build every cue now (tests; normal play builds lazily + prewarms). */
export function buildAll(): void {
  for (const k of Object.keys(recipes)) ensure(k);
}

/** Peak across a cue's variants (test hook: < 1.0). */
export function cuePeak(name: string): number {
  ensure(name);
  let peak = 0;
  for (const data of rendered[name])
    for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i]));
  return peak;
}

/** Rendered length of a cue in seconds (variant 0; TIER_RULES test hook). */
export function cueSeconds(name: string): number {
  ensure(name);
  return rendered[name][0].length / RATE;
}

/** HF-weighted RMS in dB — the measure cues are normalized on. */
export function cueLoudnessDb(name: string): number {
  ensure(name);
  return 20 * Math.log10(Math.max(weightedRms(rendered[name][0]), 1e-9));
}

export function variantCount(name: string): number {
  ensure(name);
  return rendered[name].length;
}

// ------------------------------------------------------------- synth core

interface ToneOpts {
  f0?: number; f1?: number; partials?: number[][]; amp?: number;
  attack?: number; curve?: number; damp?: number; decay_scale?: number;
  detune?: number; vib_hz?: number; vib_depth?: number;
  trem_hz?: number; trem_depth?: number;
}

function mkbuf(dur: number): Float32Array {
  return new Float32Array(Math.floor(dur * RATE));
}

/** Shared envelope: linear attack (>=3ms, no clicks) then pow-curve decay
    that reaches exactly zero at the end. */
function env(t: number, dur: number, attack: number, curve: number): number {
  const a = Math.max(attack, 0.003);
  if (t < a) return t / a;
  const rem = 1.0 - (t - a) / Math.max(dur - a, 0.001);
  return Math.pow(Math.max(rem, 0.0), curve);
}

/** Additive partial stack — see sfx.gd tone() for the two partial flavors
    ([ratio, amp] shared-envelope / [ratio, amp, decay] measured modes),
    two-voice micro-detune, and Nyquist culling at max pitch bend. */
function tone(buf: Float32Array, at: number, dur: number, opts: ToneOpts): void {
  const f0 = (opts.f0 ?? 440.0) * variantTune;
  const f1 = (opts.f1 ?? opts.f0 ?? 440.0) * variantTune;
  const partials = opts.partials ?? BAR;
  const amp = opts.amp ?? 1.0;
  const attack = opts.attack ?? 0.004;
  const curve = opts.curve ?? 2.0;
  const damp = opts.damp ?? 9.0;
  const dscale = opts.decay_scale ?? 1.0;
  const detune = opts.detune ?? 0.0018;
  const vibHz = opts.vib_hz ?? 0.0;
  const vibDepth = opts.vib_depth ?? 0.0;
  const tremHz = opts.trem_hz ?? 0.0;
  const tremDepth = opts.trem_depth ?? 0.0;
  const start = Math.floor(at * RATE);
  const n = Math.min(Math.floor(dur * RATE), buf.length - start);
  if (n <= 0) return;
  const nyqSafe = (RATE * 0.5 * 0.95) / PITCH_HEADROOM;
  const fmax = Math.max(f0, f1) * (1.0 + vibDepth);
  const stack = partials.filter((p) => fmax * p[0] * (1.0 + detune) < nyqSafe);
  const a = Math.max(attack, 0.003);
  let phase = 0.0;
  for (let i = 0; i < n; i++) {
    const t = i / RATE;
    let freq = f0 + (f1 - f0) * (t / dur);
    if (vibHz > 0.0) freq *= 1.0 + vibDepth * Math.sin(TAU * vibHz * t);
    phase += (TAU * freq) / RATE;
    const eShared = env(t, dur, attack, curve);
    // measured modes bring their own decay: attack ramp + end taper only
    const eModal = (t < a ? t / a : 1.0) *
      Math.max(0.0, Math.min(1.0, (dur - t) / 0.015));
    let trem = 1.0;
    if (tremHz > 0.0) trem = 1.0 - tremDepth * 0.5 * (1.0 + Math.sin(TAU * tremHz * t));
    let v = 0.0;
    for (const p of stack) {
      let pg = p[1];
      if (p.length >= 3) pg *= eModal * Math.exp(-t * p[2] * dscale);
      else {
        pg *= eShared;
        if (damp > 0.0) pg *= Math.exp(-t * damp * (p[0] - 1.0));
      }
      if (detune > 0.0)
        v += (Math.sin(phase * p[0] * (1.0 + detune)) +
          Math.sin(phase * p[0] * (1.0 - detune))) * 0.5 * pg;
      else v += Math.sin(phase * p[0]) * pg;
    }
    buf[start + i] += v * amp * trem;
  }
}

interface NoiseOpts {
  amp?: number; attack?: number; curve?: number; lp?: number;
  bp_hz?: number; bp_q?: number;
}

/** Filtered-noise breath. Default: one-pole low-passed hiss. With bp_hz set:
    state-variable band-pass — a pitched "chiff" that reads as material
    contact rather than static. */
function noise(buf: Float32Array, at: number, dur: number, opts: NoiseOpts): void {
  const amp = opts.amp ?? 0.5;
  const attack = opts.attack ?? 0.002;
  const curve = opts.curve ?? 2.5;
  const lp = opts.lp ?? 0.3;
  const bpHz = opts.bp_hz ?? 0.0;
  const start = Math.floor(at * RATE);
  const n = Math.min(Math.floor(dur * RATE), buf.length - start);
  if (n <= 0) return;
  if (bpHz > 0.0) {
    const f = 2.0 * Math.sin((Math.PI * Math.max(60.0, Math.min(bpHz, RATE / 6.5))) / RATE);
    const q1 = 1.0 / Math.max(opts.bp_q ?? 1.1, 0.4);
    let low = 0.0;
    let band = 0.0;
    for (let i = 0; i < n; i++) {
      const t = i / RATE;
      low += f * band;
      const high = Math.random() * 2 - 1 - low - q1 * band;
      band += f * high;
      buf[start + i] += band * 2.0 * env(t, dur, attack, curve) * amp;
    }
    return;
  }
  let y = 0.0;
  for (let i = 0; i < n; i++) {
    const t = i / RATE;
    y += lp * (Math.random() * 2 - 1 - y);
    buf[start + i] += y * env(t, dur, attack, curve) * amp;
  }
}

/** THE family voice: a struck-bone hit — BAR partials + a matched band-passed
    noise chiff whose center follows the pitch. */
function strike(buf: Float32Array, at: number, f0: number, dur: number,
  amp = 1.0, opts: ToneOpts & { noise?: number } = {}): void {
  const o: ToneOpts = { ...opts, f0, amp };
  if (!o.partials) o.partials = BAR;
  tone(buf, at, dur, o);
  noise(buf, at, Math.min(dur, 0.025), {
    amp: amp * (opts.noise ?? 0.35),
    bp_hz: Math.max(300.0, Math.min(f0 * 2.5, 6500.0)),
  });
}

/** Pentatonic scale note i (A-minor penta from A3). */
function note(i: number): number {
  const octave = Math.floor(i / 5);
  const semis = PENTA[i % 5] + 12 * octave;
  return ROOT_HZ * Math.pow(2.0, semis / 12.0);
}

/** Brightness-aware soft saturation + HF-weighted loudness normalization —
    see sfx.gd finish() for the reasoning. Guarantee: peak <= PEAK < 1.0. */
function finish(buf: Float32Array): Float32Array {
  const drive = SOFT_DRIVE;
  // zero-crossing estimate of dominant frequency -> how much tanh to blend
  let zc = 0;
  for (let i = 1; i < buf.length; i++)
    if (buf[i] >= 0 !== buf[i - 1] >= 0) zc++;
  const estHz = (zc * RATE) / Math.max(2.0, 2.0 * buf.length);
  const sat = 1.0 - Math.max(0.0, Math.min(1.0, (estHz - SAT_LO_HZ) / (SAT_HI_HZ - SAT_LO_HZ)));
  const norm = Math.tanh(drive);
  let peak = 0.0;
  for (let i = 0; i < buf.length; i++) {
    const v = buf[i] + ((Math.tanh(buf[i] * drive) / norm) - buf[i]) * sat;
    buf[i] = v;
    peak = Math.max(peak, Math.abs(v));
  }
  const targetRms = Math.pow(10, RMS_DB / 20);
  let gain = targetRms / Math.max(weightedRms(buf), 0.00001);
  gain = Math.min(gain, PEAK / Math.max(peak, 0.00001));
  const scale = 32000.0 / 32768.0; // match the original 16-bit packing level
  for (let i = 0; i < buf.length; i++)
    buf[i] = Math.max(-1.0, Math.min(1.0, buf[i] * gain)) * scale;
  return buf;
}

/** RMS with content above HF_HZ counted HF_WEIGHT× hotter (one-pole band
    split) — cheap stand-in for equal-loudness weighting. */
function weightedRms(buf: Float32Array): number {
  const a = 1.0 - Math.exp((-TAU * HF_HZ) / RATE);
  let lo = 0.0;
  let acc = 0.0;
  for (let i = 0; i < buf.length; i++) {
    lo += a * (buf[i] - lo);
    const w = buf[i] + (buf[i] - lo) * (HF_WEIGHT - 1.0);
    acc += w * w;
  }
  return Math.sqrt(acc / Math.max(1.0, buf.length));
}

// ------------------------------------------------------------- cue recipes
// All durations short; celebrations ring longer. Direct ports of sfx.gd.

recipes['ui_tick'] = () => {
  const b = mkbuf(0.045);
  strike(b, 0.0, 1174.7, 0.045, 0.5, { noise: 0.15, curve: 2.6 });
  return b;
};
recipes['ui_tab'] = () => {
  const b = mkbuf(0.09);
  strike(b, 0.0, 1174.7, 0.05, 0.45, { noise: 0.12, curve: 2.4 });
  strike(b, 0.035, 1568.0, 0.05, 0.4, { noise: 0.1, curve: 2.4 });
  return b;
};
recipes['deny'] = () => {
  const b = mkbuf(0.22);
  tone(b, 0.0, 0.22, { f0: 110.0, partials: [[1.0, 1.0], [3.0, 0.5], [5.0, 0.22]],
    attack: 0.006, curve: 1.2, damp: 1.5, trem_hz: 27.0, trem_depth: 0.7 });
  return b;
};
recipes['deny_soft'] = () => {
  const b = mkbuf(0.1);
  tone(b, 0.0, 0.1, { f0: 123.5, partials: [[1.0, 1.0], [3.0, 0.35]],
    attack: 0.005, curve: 1.5, damp: 1.5, trem_hz: 27.0, trem_depth: 0.6, amp: 0.6 });
  return b;
};
recipes['claim'] = () => {
  const b = mkbuf(0.5);
  for (let i = 0; i < 3; i++)
    strike(b, 0.09 * i, note([0, 2, 4][i]) * 2.0, 0.26, 0.8, { decay_scale: 0.35 });
  return b;
};
recipes['level_up'] = () => {
  const b = mkbuf(0.75);
  const seq = [0, 1, 2, 4];
  for (let i = 0; i < seq.length; i++)
    strike(b, 0.085 * i, note(seq[i]) * 2.0, 0.32, 0.75, { decay_scale: 0.35 });
  strike(b, 0.085 * seq.length, note(7) * 2.0, 0.35, 0.5, { noise: 0.1, decay_scale: 0.3 });
  return b;
};
recipes['level_up_big'] = () => {
  const b = mkbuf(1.15);
  const seq = [0, 1, 2, 3, 4, 5];
  for (let i = 0; i < seq.length; i++)
    strike(b, 0.08 * i, note(seq[i]) * 2.0, 0.34, 0.72, { decay_scale: 0.35 });
  // octave chord tail: root + fifth + octave ringing together (bell voice)
  for (const j of [5, 7, 10])
    strike(b, 0.52, note(j) * 2.0, 0.6, 0.5, { partials: BELL, decay_scale: 0.4, noise: 0.08 });
  return b;
};
recipes['fanfare'] = () => {
  const b = mkbuf(1.35);
  const seq = [0, 2, 4];
  for (let i = 0; i < seq.length; i++)
    strike(b, 0.075 * i, note(seq[i]) * 2.0, 0.3, 0.7, { decay_scale: 0.35 });
  for (const j of [5, 7, 9]) // high chord, long ring — the first-time moment
    strike(b, 0.28, note(j) * 2.0, 1.0, 0.55, { partials: BELL, decay_scale: 0.35, noise: 0.08 });
  strike(b, 0.5, note(10) * 2.0, 0.8, 0.4, { curve: 1.6 });
  return b;
};
recipes['break_soft'] = () => {
  const b = mkbuf(0.14);
  strike(b, 0.0, 294.0, 0.12, 0.6, { noise: 0.9, curve: 3.0 });
  return b;
};
recipes['break_alarm'] = () => {
  const b = mkbuf(0.8);
  // the crack...
  strike(b, 0.0, 247.0, 0.1, 1.0, { noise: 1.1, curve: 3.0 });
  noise(b, 0.0, 0.08, { amp: 0.7, lp: 0.6 });
  // ...then the two-tone alarm, harsh minor second, repeated
  for (let i = 0; i < 2; i++) {
    tone(b, 0.14 + 0.3 * i, 0.16, { f0: 466.2, partials: METAL, attack: 0.004, amp: 0.8 });
    tone(b, 0.28 + 0.3 * i, 0.16, { f0: 415.3, partials: METAL, attack: 0.004, amp: 0.8 });
  }
  return b;
};
recipes['quest_start'] = () => {
  const b = mkbuf(0.55);
  strike(b, 0.0, note(0) * 2.0, 0.3, 0.7, { curve: 2.0 });
  strike(b, 0.13, note(4) * 2.0, 0.4, 0.65, { curve: 1.9 });
  return b;
};
recipes['quest_step'] = () => {
  const b = mkbuf(0.3);
  strike(b, 0.0, note(2) * 2.0, 0.18, 0.6, { curve: 2.2 });
  strike(b, 0.07, note(4) * 2.0, 0.22, 0.55, { curve: 2.2 });
  return b;
};
recipes['quest_done'] = () => {
  const b = mkbuf(0.85);
  const seq = [0, 2, 4, 5];
  for (let i = 0; i < seq.length; i++)
    strike(b, 0.08 * i, note(seq[i]) * 2.0, 0.3, 0.7, { decay_scale: 0.35 });
  strike(b, 0.34, note(7) * 2.0, 0.5, 0.55, { decay_scale: 0.3 });
  return b;
};
recipes['xp'] = () => {
  const b = mkbuf(0.055);
  strike(b, 0.0, 1318.5, 0.055, 0.35, { noise: 0.08, curve: 2.8 });
  return b;
};
recipes['pluck'] = () => {
  const b = mkbuf(0.08);
  strike(b, 0.0, 987.8, 0.08, 0.5, { noise: 0.15, curve: 2.4 });
  return b;
};
recipes['empty'] = () => {
  const b = mkbuf(0.2);
  tone(b, 0.0, 0.2, { f0: 196.0, partials: [[1.0, 1.0], [1.5, 0.4]],
    attack: 0.005, curve: 2.6, amp: 0.8 });
  return b;
};

// combat

// Hit thuds reshaped to stoneheim's character (sound.js sfx.hit): a dry
// band-passed noise burst (900 Hz out / 480 Hz in, Q 0.7, 90ms) over a
// falling triangle gulp (210->90 out / 140->60 in, 130ms) — deeper and
// darker when it's YOUR blood. Amp ratio noise:tone = 0.34:0.22 as there.
const TRI: number[][] = [[1.0, 1.0], [3.0, 0.111], [5.0, 0.04]]; // triangle osc
recipes['hit_out'] = () => {
  const b = mkbuf(0.13);
  noise(b, 0.0, 0.09, { amp: 0.7, attack: 0.002, curve: 2.5, bp_hz: 900.0, bp_q: 0.7 });
  tone(b, 0.0, 0.13, { f0: 210.0, f1: 90.0, partials: TRI, attack: 0.008, curve: 2.5, amp: 0.45 });
  return b;
};
recipes['hit_in'] = () => {
  const b = mkbuf(0.13);
  noise(b, 0.0, 0.09, { amp: 0.7, attack: 0.002, curve: 2.5, bp_hz: 480.0, bp_q: 0.7 });
  tone(b, 0.0, 0.13, { f0: 140.0, f1: 60.0, partials: TRI, attack: 0.008, curve: 2.5, amp: 0.45 });
  return b;
};
recipes['miss'] = () => {
  const b = mkbuf(0.18);
  noise(b, 0.0, 0.18, { amp: 0.8, attack: 0.05, curve: 2.0, lp: 0.5 });
  tone(b, 0.0, 0.18, { f0: 520.0, f1: 240.0, partials: [[1.0, 1.0]],
    attack: 0.05, curve: 2.0, amp: 0.14 });
  return b;
};
recipes['block'] = () => {
  const b = mkbuf(0.09);
  strike(b, 0.0, 494.0, 0.07, 0.9, { partials: METAL, noise: 0.5 });
  return b;
};
recipes['kill'] = () => {
  const b = mkbuf(0.55);
  strike(b, 0.0, 110.0, 0.18, 1.0, { noise: 0.6, curve: 2.2, attack: 0.002 });
  strike(b, 0.09, note(5) * 2.0, 0.28, 0.55, { decay_scale: 0.35 });
  strike(b, 0.17, note(7) * 2.0, 0.34, 0.5, { decay_scale: 0.35 });
  return b;
};
recipes['death'] = () => {
  const b = mkbuf(1.1);
  const freqs = [note(4), note(2), note(0)];
  for (let i = 0; i < freqs.length; i++)
    tone(b, 0.22 * i, 0.55, { f0: freqs[i], f1: freqs[i] * 0.94, partials: DARK,
      attack: 0.02, curve: 1.6, amp: 0.8, vib_hz: 5.0, vib_depth: 0.008 });
  tone(b, 0.62, 0.45, { f0: note(0) * 0.5, partials: DARK, attack: 0.03, curve: 1.4, amp: 0.7 });
  return b;
};
recipes['fled'] = () => {
  const b = mkbuf(0.35);
  tone(b, 0.0, 0.32, { f0: 494.0, f1: 233.0, partials: [[1.0, 1.0], [2.0, 0.15]],
    attack: 0.015, curve: 1.8, amp: 0.7 });
  noise(b, 0.02, 0.3, { amp: 0.35, attack: 0.06, curve: 2.0, lp: 0.45 });
  return b;
};
recipes['eat'] = () => {
  const b = mkbuf(0.3);
  tone(b, 0.0, 0.12, { f0: 175.0, f1: 98.0, partials: DARK, attack: 0.008, curve: 2.0, amp: 0.9 });
  tone(b, 0.15, 0.1, { f0: 147.0, f1: 87.0, partials: DARK, attack: 0.008, curve: 2.0, amp: 0.6 });
  return b;
};
recipes['heal'] = () => {
  const b = mkbuf(0.35);
  tone(b, 0.0, 0.35, { f0: 392.0, f1: 587.0, partials: [[1.0, 1.0], [2.0, 0.2]],
    attack: 0.05, curve: 1.6, amp: 0.7, vib_hz: 6.0, vib_depth: 0.01 });
  return b;
};
recipes['arrived'] = () => {
  const b = mkbuf(0.5);
  strike(b, 0.0, 392.0, 0.28, 0.7, { decay_scale: 0.4, noise: 0.12 });
  strike(b, 0.13, 523.3, 0.36, 0.65, { decay_scale: 0.4, noise: 0.1 });
  return b;
};

// work families — ONE soft contact each ("constant" tier): a single
// bump/tick/thock in the family material, never a figure or a bed.

recipes['work_gather'] = () => {
  const b = mkbuf(0.09);
  strike(b, 0.0, 523.3, 0.08, 0.55, { noise: 0.35, curve: 2.6 });
  return b;
};
recipes['work_mine'] = () => {
  // one pick hit: low thud + faint metal ping in the same instant
  const b = mkbuf(0.1);
  strike(b, 0.0, 98.0, 0.09, 1.0, { noise: 0.5, curve: 2.4, attack: 0.002 });
  strike(b, 0.004, 1397.0, 0.04, 0.15, { partials: METAL, noise: 0.2 });
  return b;
};
recipes['work_chop'] = () => {
  const b = mkbuf(0.1);
  strike(b, 0.0, 165.0, 0.09, 1.0,
    { partials: [[1.0, 1.0], [2.3, 0.3]], noise: 0.7, curve: 2.8, attack: 0.002 });
  return b;
};
recipes['work_fish'] = () => {
  // one soft bloop
  const b = mkbuf(0.1);
  tone(b, 0.0, 0.09, { f0: 330.0, f1: 470.0, partials: [[1.0, 1.0]],
    attack: 0.008, curve: 2.2, amp: 0.8 });
  return b;
};
recipes['work_hunt'] = () => {
  // one muffled footfall
  const b = mkbuf(0.08);
  noise(b, 0.0, 0.07, { amp: 0.55, attack: 0.008, curve: 2.6, lp: 0.7 });
  strike(b, 0.0, 587.3, 0.05, 0.3, { noise: 0.2, curve: 3.0 });
  return b;
};
recipes['work_smelt'] = () => {
  // one low furnace thump
  const b = mkbuf(0.13);
  tone(b, 0.0, 0.12, { f0: 130.8, partials: DARK, attack: 0.01, curve: 1.8, amp: 0.8 });
  noise(b, 0.0, 0.06, { amp: 0.2, lp: 0.18 });
  return b;
};
recipes['work_smith'] = () => {
  // one hammer tap — damped hard so it can't ring over itself
  const b = mkbuf(0.1);
  strike(b, 0.0, 660.0, 0.09, 0.85,
    { partials: METAL, noise: 0.4, attack: 0.002, decay_scale: 3.0 });
  return b;
};
recipes['work_whittle'] = () => {
  // one knife shave
  const b = mkbuf(0.08);
  noise(b, 0.0, 0.07, { amp: 0.6, attack: 0.012, curve: 2.2, lp: 0.5 });
  return b;
};
recipes['work_knap'] = () => {
  const b = mkbuf(0.06);
  strike(b, 0.0, 1244.5, 0.045, 0.8, { noise: 0.9, curve: 3.4, attack: 0.001 });
  return b;
};
recipes['work_fletch'] = () => {
  // one nock tick
  const b = mkbuf(0.07);
  strike(b, 0.0, 1046.5, 0.06, 0.45, { noise: 0.15, curve: 2.8 });
  return b;
};
recipes['work_cook'] = () => {
  // one soft pot tap
  const b = mkbuf(0.09);
  strike(b, 0.0, 392.0, 0.08, 0.55, { noise: 0.25, curve: 2.4 });
  return b;
};
recipes['work_sew'] = () => {
  // one needle tick
  const b = mkbuf(0.06);
  strike(b, 0.0, 1568.0, 0.05, 0.4, { noise: 0.1, curve: 3.0 });
  return b;
};
recipes['work_mould'] = () => {
  // one dull clay press
  const b = mkbuf(0.1);
  tone(b, 0.0, 0.09, { f0: 220.0, f1: 185.0, partials: DARK, attack: 0.012, curve: 2.0, amp: 0.8 });
  return b;
};
recipes['work_craft'] = () => {
  const b = mkbuf(0.1);
  strike(b, 0.0, 440.0, 0.09, 0.7, { noise: 0.35, curve: 2.6 });
  return b;
};
